import React, { useMemo } from "react";
import { Alert, Box, Grid, Stack, Typography } from "@mui/material";
import { Field } from "formik";
import { FullWidthField } from "@poseidon/ui/components/form/FullWidthField";
import InputAdornment from "@mui/material/InputAdornment";
import * as Yup from "yup";
import { EditSailorFormPanel } from "./EditSailorFormPanel";

const incomeFields = {
  income: "Net Income (Monthly)",
  otherIncome: "Other Income (Monthly)",
};

const outgoingFields = {
  mortgageRent: "Mortgage/Rent (Monthly)",
  loans: "Loan Repayments (Monthly)",
  otherCommitments: "Other Commitments (Monthly)",
};

const assetFields = {
  mainResidenceValue: "Main Residence Value",
  otherPropertyValue: "Other Property Value",
  instantAccessSavings: "Instant Access Savings",
  investmentsAndDeposits: "Investments and Deposits",
  carsValue: "Cars Value",
  otherValuableAssets: "Other Valuable Assets",
};
const liabilityFields = {
  mainResidenceMortgage: "Main Residence Mortgage",
  otherMortgages: "Other Mortgages",
  loansHpCarFinanceValue: "Loans/HP Car Finance Value",
  totalCreditCardLimits: "Total Credit Card Limits",
  dependants: "No of dependants",
  creditCardsBalance: "Credit Cards Balance",
  buyNowPayLaterBalance: "Buy Now - Pay Later Balance",
};

/*
not used in affordability calculator:
mortgageRent
loans
mainResidenceValue
otherPropertyValue
instantAccessSavings
investmentsAndDeposits
carsValue
otherValuableAssets
totalCreditCardLimits

not seen in schema for mobile app:
otherIncome
 */
const FieldSection = ({ title, fields }) => {
  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, fontSize: "1em" }}>
        {title}
      </Typography>
      <Grid container spacing={2}>
        {Object.entries(fields).map(([field, label]) => (
          <Grid item xs={12} sm={6} key={field}>
            <Field
              component={FullWidthField}
              name={field}
              label={label}
              margin="none"
              {...(field !== "dependants"
                ? {
                    InputProps: {
                      startAdornment: (
                        <InputAdornment position="start">£</InputAdornment>
                      ),
                      type: "number",
                    },
                  }
                : {
                    InputProps: {
                      type: "number",
                    },
                  })}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

const sailorDefaults = {
  dependants: 0,
  income: 0,
  mortgageRent: 0,
  loans: 0,
  creditCardsBalance: 0,
  otherCommitments: 0,
  buyNowPayLaterBalance: 0,
  mainResidenceValue: 0,
  otherPropertyValue: 0,
  idDocumentIssuingCountry: "",
  instantAccessSavings: 0,
  investmentsAndDeposits: 0,
  carsValue: 0,
  otherValuableAssets: 0,
  mainResidenceMortgage: 0,
  otherMortgages: 0,
  loansHpCarFinanceValue: 0,
  totalCreditCardLimits: 0,
};

export const validationSchema = Yup.object().shape({
  income: Yup.number().optional().min(0).default(0),
  otherIncome: Yup.number().optional().min(0).default(0),
  mortgageRent: Yup.number().optional().min(0).default(0),
  loans: Yup.number().optional().min(0).default(0),
  otherCommitments: Yup.number().optional().min(0).default(0),
  mainResidenceValue: Yup.number().optional().min(0).default(0),
  otherPropertyValue: Yup.number().optional().min(0).default(0),
  instantAccessSavings: Yup.number().optional().min(0).default(0),
  investmentsAndDeposits: Yup.number().optional().min(0).default(0),
  carsValue: Yup.number().optional().min(0).default(0),
  otherValuableAssets: Yup.number().optional().min(0).default(0),
  mainResidenceMortgage: Yup.number().optional().min(0).default(0),
  otherMortgages: Yup.number().optional().min(0).default(0),
  loansHpCarFinanceValue: Yup.number().optional().min(0).default(0),
  totalCreditCardLimits: Yup.number().optional().min(0).default(0),
  dependants: Yup.number().optional().min(0).default(0),
  creditCardsBalance: Yup.number().optional().min(0).default(0),
  buyNowPayLaterBalance: Yup.number().optional().min(0).default(0),
});

export function IncomeFields({ readonly, sailor }) {
  const initialValues = useMemo(
    () => ({
      ...sailorDefaults,
      ...sailor,
    }),
    [sailor]
  );
  return (
    <EditSailorFormPanel
      title="Finances"
      disabled={readonly}
      initialValues={initialValues}
      validationSchema={validationSchema}
      sailor={sailor}
    >
      <Stack spacing={3}>
        <FieldSection title="Income" fields={incomeFields} />
        <FieldSection title="Outgoings" fields={outgoingFields} />
        <FieldSection title="Assets" fields={assetFields} />
        <FieldSection title="Liabilities" fields={liabilityFields} />
        <Alert severity="warning" sx={{ width: 1 }}>
          Note that changes to this section will impact the affordability report
          for any applications for this sailor - make sure you re-run to reflect
          the latest values
        </Alert>
      </Stack>
    </EditSailorFormPanel>
  );
}
